/* eslint-disable no-console */
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/ConfirmationDialog';
import { Table, TableRef } from '../../../components/Table/Table';
import { useAppDispatch } from '../../../redux/hooks';
import {
  deleteCompanyGroup,
  fetchCompanyGroups,
  uploadCompanyGroupsCsv,
} from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.actions';
import {
  companyGroupSelector,
  companyGroupsLoadingSelector,
  companyGroupUploadCsvLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.selectors';
import { round2Format } from '../../../utils/number';
import { locations } from '../../../routes/locations';
import { downloadCsvTemplateByName } from '../../../utils/csvTemplates';

export const CompanyGroups = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { companyGroups, totalCompanyGroups } = useSelector(companyGroupSelector);
  const isLoading = useSelector(companyGroupsLoadingSelector);
  const isUploadLoading = useSelector(companyGroupUploadCsvLoadingSelector);
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloadTemplateLoading, setIsDownloadTemplateLoading] = useState(false);

  const onAddNew = (): void => {
    if (companyId) {
      navigate(locations.companyGroup(Number(companyId), 'create'));
    }
  };

  const onUploadCsv = (): void => {
    setUploadDialogOpen(true);
  };

  const onDownloadCsv = async (): Promise<void> => {
    try {
      setIsDownloadTemplateLoading(true);
      await downloadCsvTemplateByName('company_groups');
    } catch (error: any) {
      const message = error?.message || 'Failed to download CSV template.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsDownloadTemplateLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !companyId) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }

    try {
      await dispatch(uploadCompanyGroupsCsv({ companyId: Number(companyId), file: selectedFile })).unwrap();
      enqueueSnackbar('CSV file uploaded successfully!', { variant: 'success' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh the company groups list
      dispatch(fetchCompanyGroups({ companyId: Number(companyId) }));
    } catch (err: any) {
      enqueueSnackbar(`Upload failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onEditGroup = (groupId: number): void => {
    if (companyId) {
      navigate(locations.companyGroup(Number(companyId), groupId.toString()));
    }
  };

  // const onDeleteGroup = async (groupId: number): Promise<void> => {
  //   const isConfirmed = await asyncDialogRef.current?.show({
  //     title: 'Delete Company Group',
  //     description: 'Are you sure you want to delete this company group?',
  //     onConfirm: async () => {
  //       if (companyId) {
  //         await dispatch(deleteCompanyGroup({ companyId: Number(companyId), groupId })).unwrap();
  //         enqueueSnackbar('Company group deleted successfully!', { variant: 'success' });
  //         dispatch(fetchCompanyGroups({ companyId: Number(companyId) }));
  //       }
  //     },
  //   });

  //   // Error handling is done in the dialog's onConfirm callback
  //   if (!isConfirmed && companyId) {
  //     // User cancelled, no action needed
  //   }
  // };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      width: 100,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`edit-company-group-${params.row.id}`}
          icon={<EditOutlinedIcon />}
          label="Edit"
          onClick={() => onEditGroup(params.row.id)}
        />,
        // <GridActionsCellItem
        //   key={`delete-company-group-${params.row.id}`}
        //   icon={<DeleteIcon />}
        //   label="Delete"
        //   onClick={() => onDeleteGroup(params.row.id)}
        // />,
      ],
    },
    {
      field: 'id',
      headerName: 'Id',
      width: 70,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'contribution',
      headerName: 'Contribution',
      width: 120,
      valueFormatter: (params) => round2Format(Number(params.value)),
    },
    {
      field: 'maxCap',
      headerName: 'Max Cap',
      width: 150,
      valueFormatter: (params) => round2Format(Number(params.value)),
    },
    {
      field: 'lockupPeriod',
      headerName: 'Lockup Period (Days)',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'coContributionDueDate',
      headerName: 'Co-Contribution Due Date',
      width: 200,
      type: 'date',
      valueFormatter: (params) => params.value,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'numberOfWithdrawalsAllowed',
      headerName: 'Withdrawals Allowed',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Company Groups &nbsp;
          <Fab color="primary" size="small" aria-label="Create Company Group" onClick={onAddNew} sx={{ mr: 1 }}>
            <AddIcon />
          </Fab>
          <Fab
            color="secondary"
            size="small"
            aria-label="Upload CSV"
            onClick={onUploadCsv}
            sx={{ color: 'common.white' }}
          >
            <UploadFileIcon />
          </Fab>
          <Fab
            color="secondary"
            size="small"
            aria-label="Download CSV"
            onClick={onDownloadCsv}
            sx={{ mx: 1 }}
            disabled={isDownloadTemplateLoading}
          >
            {isDownloadTemplateLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SimCardDownloadOutlinedIcon sx={{ color: 'common.white' }} />
            )}
          </Fab>
        </Typography>
      </Box>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle>Upload Company Groups CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              aria-label="Select CSV file"
              color="primary"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Select CSV File'}
              <input ref={fileInputRef} hidden accept=".csv,text/csv" type="file" onChange={handleFileChange} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} color="secondary">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleUpload}
            variant="contained"
            color="primary"
            loading={isUploadLoading}
            disabled={!selectedFile}
          >
            Upload
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Table
        ref={tableRef}
        columns={columns}
        rows={companyGroups}
        rowCount={totalCompanyGroups}
        fetchItems={fetchCompanyGroups}
        fetchItemsCustomParams={{ companyId: Number(companyId) }}
        loading={isLoading}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `company-group-${Date.now()}` },
          excelOptions: { fileName: `company-group-${Date.now()}` },
        }}
      />

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
};
